import React, { useState, useEffect } from 'react'
import { 
  User, 
  Mail, 
  Edit3, 
  Save, 
  X, 
  Camera, 
  Settings, 
  Shield, 
  Calendar,
  LogOut,
  Eye,
  EyeOff
} from 'lucide-react'
import AuthService from '../services/authService'
import toast from 'react-hot-toast'

export const UserProfile = ({ isOpen, onClose, onLogout }) => {
  const [user, setUser] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({})
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (isOpen) {
      const currentUser = AuthService.getCurrentUser()
      setUser(currentUser)
      setEditData({
        displayName: currentUser?.profile?.displayName || '',
        bio: currentUser?.profile?.bio || '',
        email: currentUser?.email || ''
      })
    }
  }, [isOpen])

  // 处理编辑数据变化
  const handleEditChange = (e) => {
    const { name, value } = e.target
    setEditData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // 清除相关错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // 处理密码数据变化
  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // 清除相关错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // 验证编辑数据
  const validateEditData = () => {
    const newErrors = {}

    if (!editData.displayName.trim()) {
      newErrors.displayName = '显示名称不能为空'
    }

    if (!editData.email.trim()) {
      newErrors.email = '邮箱不能为空'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.email)) {
      newErrors.email = '邮箱格式不正确'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 验证密码数据
  const validatePasswordData = () => {
    const newErrors = {}

    if (!passwordData.oldPassword) {
      newErrors.oldPassword = '旧密码不能为空'
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = '新密码不能为空'
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = '新密码至少8个字符'
    } else if (!/(?=.*[A-Za-z])(?=.*\d)/.test(passwordData.newPassword)) {
      newErrors.newPassword = '新密码必须包含字母和数字'
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = '两次密码输入不一致'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 保存编辑
  const handleSaveEdit = async () => {
    if (!validateEditData()) return

    setIsLoading(true)
    try {
      const result = await AuthService.updateProfile(editData)
      if (result.success) {
        setUser(result.user)
        setIsEditing(false)
        toast.success('资料更新成功！')
      } else {
        toast.error(result.error || '更新失败')
      }
    } catch (error) {
      toast.error('更新时发生错误')
    } finally {
      setIsLoading(false)
    }
  }

  // 修改密码
  const handleChangePassword = async () => {
    if (!validatePasswordData()) return

    setIsLoading(true)
    try {
      const result = await AuthService.changePassword(
        passwordData.oldPassword,
        passwordData.newPassword
      )
      if (result.success) {
        setShowPasswordForm(false)
        setPasswordData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        toast.success('密码修改成功！')
      } else {
        toast.error(result.error || '密码修改失败')
      }
    } catch (error) {
      toast.error('修改密码时发生错误')
    } finally {
      setIsLoading(false)
    }
  }

  // 处理登出
  const handleLogout = async () => {
    try {
      await AuthService.logout()
      onLogout?.()
      onClose()
      toast.success('已安全登出')
    } catch (error) {
      toast.error('登出时发生错误')
    }
  }

  // 取消编辑
  const handleCancelEdit = () => {
    setEditData({
      displayName: user?.profile?.displayName || '',
      bio: user?.profile?.bio || '',
      email: user?.email || ''
    })
    setIsEditing(false)
    setErrors({})
  }

  if (!isOpen || !user) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-dark-light/95 backdrop-blur-xl rounded-3xl p-8 w-full max-w-2xl border border-white/10 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">个人资料</h2>
          <button
            onClick={onClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧：头像和基本信息 */}
          <div className="lg:col-span-1">
            <div className="text-center">
              {/* 头像 */}
              <div className="relative inline-block mb-4">
                <div className="w-24 h-24 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {user.profile?.displayName?.charAt(0) || user.username?.charAt(0) || 'U'}
                </div>
                <button className="absolute bottom-0 right-0 p-2 bg-dark-light border border-white/20 rounded-full text-white hover:bg-white/10 transition-all duration-300">
                  <Camera size={16} />
                </button>
              </div>

              {/* 基本信息 */}
              <h3 className="text-xl font-semibold text-white mb-1">
                {user.profile?.displayName || user.username}
              </h3>
              <p className="text-white/70 text-sm mb-4">@{user.username}</p>

              {/* 角色和权限 */}
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Shield size={16} className="text-cyan-400" />
                  <span className="text-white/70">角色：</span>
                  <span className="text-cyan-400 capitalize">{user.role}</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Calendar size={16} className="text-cyan-400" />
                  <span className="text-white/70">加入时间：</span>
                  <span className="text-cyan-400">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：详细信息 */}
          <div className="lg:col-span-2">
            {!isEditing ? (
              /* 查看模式 */
              <div className="space-y-6">
                {/* 基本信息 */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="text-cyan-400" />
                    <div>
                      <p className="text-white/70 text-sm">邮箱地址</p>
                      <p className="text-white">{user.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <User size={20} className="text-cyan-400" />
                    <div>
                      <p className="text-white/70 text-sm">显示名称</p>
                      <p className="text-white">{user.profile?.displayName || '未设置'}</p>
                    </div>
                  </div>

                  {user.profile?.bio && (
                    <div className="flex items-start gap-3">
                      <Edit3 size={20} className="text-cyan-400 mt-1" />
                      <div>
                        <p className="text-white/70 text-sm">个人简介</p>
                        <p className="text-white">{user.profile.bio}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* 操作按钮 */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex-1 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Edit3 size={16} />
                    编辑资料
                  </button>
                  <button
                    onClick={() => setShowPasswordForm(true)}
                    className="flex-1 px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Settings size={16} />
                    修改密码
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <LogOut size={16} />
                    登出
                  </button>
                </div>
              </div>
            ) : (
              /* 编辑模式 */
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    显示名称
                  </label>
                  <input
                    type="text"
                    name="displayName"
                    value={editData.displayName}
                    onChange={handleEditChange}
                    className={`w-full px-4 py-3 bg-white/5 border ${
                      errors.displayName ? 'border-red-500' : 'border-white/20'
                    } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                    placeholder="请输入显示名称"
                  />
                  {errors.displayName && (
                    <p className="mt-1 text-red-400 text-sm">{errors.displayName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={editData.email}
                    onChange={handleEditChange}
                    className={`w-full px-4 py-3 bg-white/5 border ${
                      errors.email ? 'border-red-500' : 'border-white/20'
                    } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                    placeholder="请输入邮箱地址"
                  />
                  {errors.email && (
                    <p className="mt-1 text-red-400 text-sm">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    个人简介
                  </label>
                  <textarea
                    name="bio"
                    value={editData.bio}
                    onChange={handleEditChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300 resize-none"
                    placeholder="介绍一下自己..."
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSaveEdit}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-500 text-white font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>保存中...</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        保存
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <X size={16} />
                    取消
                  </button>
                </div>
              </div>
            )}

            {/* 密码修改表单 */}
            {showPasswordForm && (
              <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
                <h4 className="text-lg font-semibold text-white mb-4">修改密码</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      旧密码
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.old ? 'text' : 'password'}
                        name="oldPassword"
                        value={passwordData.oldPassword}
                        onChange={handlePasswordChange}
                        className={`w-full px-4 py-3 pr-12 bg-white/5 border ${
                          errors.oldPassword ? 'border-red-500' : 'border-white/20'
                        } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                        placeholder="请输入旧密码"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, old: !prev.old }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                      >
                        {showPasswords.old ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.oldPassword && (
                      <p className="mt-1 text-red-400 text-sm">{errors.oldPassword}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      新密码
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className={`w-full px-4 py-3 pr-12 bg-white/5 border ${
                          errors.newPassword ? 'border-red-500' : 'border-white/20'
                        } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                        placeholder="请输入新密码"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                      >
                        {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <p className="mt-1 text-red-400 text-sm">{errors.newPassword}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      确认新密码
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className={`w-full px-4 py-3 pr-12 bg-white/5 border ${
                          errors.confirmPassword ? 'border-red-500' : 'border-white/20'
                        } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                        placeholder="请再次输入新密码"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                      >
                        {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-red-400 text-sm">{errors.confirmPassword}</p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleChangePassword}
                      disabled={isLoading}
                      className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-500 text-white font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>修改中...</span>
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          修改密码
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowPasswordForm(false)
                        setPasswordData({
                          oldPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        })
                        setErrors({})
                      }}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <X size={16} />
                      取消
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserProfile

